import {
  CHUNK_SIZE,
  type GameWorld,
  MAX_CHUNK_REQUESTS_PER_FRAME,
  TILE_SIZE,
} from '@dreamgame/shared';
import { Container } from 'pixi.js';
import type { InputState } from '../input/InputManager';
import { requestChunk } from '../network/NetworkClient';
import { getChunksToRequest } from '../state/chunks';
import { isProfilerEnabled, recordSectionTime } from '../state/frameProfiler';
import {
  getCamera,
  getScreenDimensions,
  initCamera,
  resizeCamera,
  updateCameraFromInput,
} from './Camera';
import { createConstructionLayer, drawConstructionSites } from './layers/ConstructionLayer';
import { createCorpseLayer, drawCorpses, loadCorpseTextures } from './layers/CorpseLayer';
import { createDebugLayer, drawDebugHUD, drawDebugOverlays } from './layers/DebugLayer';
import {
  createEnvironmentLayer,
  drawEnvironment,
  setEnvironmentEnabled,
} from './layers/EnvironmentLayer';
import { createFogLayer, drawFog, setFogEnabled } from './layers/FogLayer';
import { createFoliageWindLayer, drawFoliageWind } from './layers/FoliageWindLayer';
import { createForestCanopyLayer, drawForestCanopy } from './layers/ForestCanopyLayer';
import {
  createForestDetailLayer,
  drawForestDetails,
  loadForestDetailTextures,
} from './layers/ForestDetailLayer';
import { createForestEffectsLayer, drawForestEffects } from './layers/ForestEffectsLayer';
import { createItemLayer, drawGroundItems, loadItemTextures } from './layers/ItemLayer';
import { createNpcLayer, drawNpcs, loadNpcTextures } from './layers/NpcLayer';
import { createObjectLayer, drawObjects, loadObjectTextures } from './layers/ObjectLayer';
import { createPathLayer, drawPaths } from './layers/PathLayer';
import { createPawnLayer, drawPawns, setLastDelta } from './layers/PawnLayer';
import {
  createProjectileLayer,
  drawProjectiles,
  loadProjectileTextures,
} from './layers/ProjectileLayer';
import {
  createSelectionLayer,
  drawSelectionBox,
  drawSelectionHighlights,
} from './layers/SelectionLayer';
import { createTerritoryLayer, drawTerritories } from './layers/TerritoryLayer';
import { createTileLayer, drawTiles, loadTileTextures } from './layers/TileLayer';
import type { VisibleChunk } from './layers/types';
import { createWaterEffectsLayer, drawWaterEffects } from './layers/WaterEffectsLayer';
import { loadPawnTextures } from './PawnAnimation';

// ============================================================================
// Renderer - Orchestrates render layers
// ============================================================================

export interface RendererOptions {
  readonly showPawns?: boolean;
  readonly showFog?: boolean;
  readonly showEnvironment?: boolean;
}

// Module state
let stage: Container;
let worldContainer: Container;
let showPawns = true;
let showFog = true;
let showEnvironment = true;

// Keep client chunk requests below server-side rate limits to avoid
// multi-second pending stalls during rapid camera movement.
const CHUNK_REQUEST_RATE_PER_SECOND = 40;
const CHUNK_REQUEST_BURST = 8;
let chunkRequestTokens = CHUNK_REQUEST_BURST;
let chunkRequestLastRefillMs = performance.now();

// ============================================================================
// PERFORMANCE: Preallocated visible chunks cache
// ============================================================================

const MAX_VISIBLE_CHUNKS = 256;

// PERFORMANCE: Object pool for chunk coords - preallocated once, never GC'd
const _chunkPool: Array<{ x: number; y: number }> = [];
for (let i = 0; i < MAX_VISIBLE_CHUNKS; i++) {
  _chunkPool.push({ x: 0, y: 0 });
}

// PERFORMANCE: Result array reused across frames. We set .length = 0 and
// re-push from the pool when bounds change. V8 keeps the backing store
// after .length = 0, so subsequent pushes up to the previous high-water
// mark are allocation-free.
const _visibleResult: Array<{ x: number; y: number }> = [];

const _cachedChunkBounds = {
  minX: Number.NaN,
  maxX: Number.NaN,
  minY: Number.NaN,
  maxY: Number.NaN,
};

// ============================================================================
// Initialization
// ============================================================================

export const createRenderer = ({
  world: _world,
  options,
}: {
  world: GameWorld;
  options?: RendererOptions;
}): { stage: Container } => {
  showPawns = options?.showPawns ?? true;
  showFog = options?.showFog ?? true;
  showEnvironment = options?.showEnvironment ?? true;

  stage = new Container();
  worldContainer = new Container();

  // Create layers
  const tileContainer = createTileLayer();
  const waterEffectsGraphics = createWaterEffectsLayer();
  const forestDetailContainer = createForestDetailLayer();
  const foliageWindContainer = createFoliageWindLayer();
  const forestEffectsGraphics = createForestEffectsLayer();
  const { graphics: territoryGraphics } = createTerritoryLayer();
  const { container: objectContainer, graphics: objectGraphics } = createObjectLayer();
  const { container: constructionContainer, graphics: constructionGraphics } =
    createConstructionLayer();
  const { container: itemContainer, graphics: itemGraphics } = createItemLayer();
  const { container: corpseContainer, graphics: corpseGraphics } = createCorpseLayer();
  const { worldGraphics: debugWorldGraphics, uiGraphics: debugUiGraphics } = createDebugLayer();
  const pathGraphics = createPathLayer();
  const { container: animalContainer, graphics: animalGraphics } = createNpcLayer();
  const { container: pawnContainer, graphics: pawnGraphics } = createPawnLayer();
  const forestCanopyGraphics = createForestCanopyLayer();
  const { container: projectileContainer, graphics: projectileGraphics } = createProjectileLayer();
  const { worldGraphics: selectionWorldGraphics, uiGraphics: selectionUiGraphics } =
    createSelectionLayer();
  const environmentGraphics = createEnvironmentLayer();
  setEnvironmentEnabled({ enabled: showEnvironment });
  const fogSprite = createFogLayer();
  setFogEnabled({ enabled: showFog });

  // Add layers to world container (bottom to top)
  stage.addChild(worldContainer);
  worldContainer.addChild(tileContainer);
  worldContainer.addChild(waterEffectsGraphics); // Water depth effects (after tiles, before territory)
  worldContainer.addChild(forestDetailContainer); // Procedural forest floor decal sprites
  worldContainer.addChild(foliageWindContainer); // Animated grass blades with wind shader
  worldContainer.addChild(forestEffectsGraphics); // Canopy shadows + ground decorations
  worldContainer.addChild(territoryGraphics); // Territory zones (below objects)
  worldContainer.addChild(objectContainer);
  worldContainer.addChild(objectGraphics);
  worldContainer.addChild(constructionContainer);
  worldContainer.addChild(constructionGraphics);
  worldContainer.addChild(itemContainer);
  worldContainer.addChild(itemGraphics);
  worldContainer.addChild(corpseContainer); // Corpses above items
  worldContainer.addChild(corpseGraphics);
  worldContainer.addChild(debugWorldGraphics);
  worldContainer.addChild(pathGraphics);
  worldContainer.addChild(animalContainer);
  worldContainer.addChild(animalGraphics);
  if (showPawns) {
    worldContainer.addChild(pawnContainer);
    worldContainer.addChild(pawnGraphics);
  }
  worldContainer.addChild(forestCanopyGraphics); // Canopy overlay above pawns
  worldContainer.addChild(projectileContainer); // Projectiles above pawns
  worldContainer.addChild(projectileGraphics);
  worldContainer.addChild(selectionWorldGraphics);

  // Screen-space layers (above world, below UI)
  stage.addChild(fogSprite); // Fog of war (screen-space alpha overlay, above world)
  stage.addChild(environmentGraphics); // Day/night ambient light overlay
  stage.addChild(selectionUiGraphics);
  stage.addChild(debugUiGraphics);

  return { stage };
};

export const initRenderer = async (): Promise<void> => {
  initCamera({ width: window.innerWidth, height: window.innerHeight });

  const textureLoads = [
    loadTileTextures(),
    loadForestDetailTextures(),
    loadObjectTextures(),
    loadItemTextures(),
    loadNpcTextures(),
    loadCorpseTextures(),
    loadProjectileTextures(),
  ];

  if (showPawns) {
    textureLoads.push(loadPawnTextures());
  }

  // Load textures in parallel
  await Promise.all(textureLoads);
};

export const resizeRenderer = ({ width, height }: { width: number; height: number }): void => {
  resizeCamera({ width, height });
};

// ============================================================================
// Update Loop
// ============================================================================

export const updateCamera = ({ input, delta }: { input: InputState; delta: number }): void => {
  updateCameraFromInput({ flags: input.flags, delta });
};

export const updateRenderer = ({ delta }: { delta: number }): void => {
  setLastDelta({ delta });

  const camera = getCamera();
  const dims = getScreenDimensions();

  // Use fractional positions for smooth camera movement.
  // Tile seam gaps are prevented by TILE_OVERLAP in TileLayer (0.5px overlap).
  worldContainer.x = dims.width / 2 - camera.x * camera.zoom;
  worldContainer.y = dims.height / 2 - camera.y * camera.zoom;
  worldContainer.scale.set(camera.zoom);
};

// ============================================================================
// Render
// ============================================================================

// Draw all layers without profiling (normal path)
const renderLayers = ({ visibleChunks }: { visibleChunks: ReadonlyArray<VisibleChunk> }): void => {
  drawTiles({ visibleChunks });
  drawWaterEffects({ visibleChunks });
  drawForestDetails({ visibleChunks });
  drawFoliageWind({ visibleChunks });
  drawForestEffects({ visibleChunks });
  drawTerritories();
  drawObjects({ visibleChunks });
  drawConstructionSites({ visibleChunks });
  drawGroundItems();
  drawCorpses();
  drawDebugOverlays({ visibleChunks });
  drawPaths();
  drawNpcs();
  if (showPawns) {
    drawPawns();
  }
  drawForestCanopy({ visibleChunks });
  drawProjectiles();
  drawSelectionHighlights();
  if (showFog) {
    drawFog();
  }
  if (showEnvironment) {
    drawEnvironment({ visibleChunks });
  }
  drawSelectionBox();
  drawDebugHUD();
};

// Draw all layers with per-layer timing instrumentation (debug path)
const renderLayersProfiled = ({
  visibleChunks,
}: {
  visibleChunks: ReadonlyArray<VisibleChunk>;
}): void => {
  let _t = performance.now();
  drawTiles({ visibleChunks });
  recordSectionTime({ name: 'tiles', durationMs: performance.now() - _t });

  _t = performance.now();
  drawWaterEffects({ visibleChunks });
  recordSectionTime({ name: 'water', durationMs: performance.now() - _t });

  _t = performance.now();
  drawForestDetails({ visibleChunks });
  recordSectionTime({ name: 'forestDetails', durationMs: performance.now() - _t });

  _t = performance.now();
  drawFoliageWind({ visibleChunks });
  recordSectionTime({ name: 'foliage', durationMs: performance.now() - _t });

  _t = performance.now();
  drawForestEffects({ visibleChunks });
  recordSectionTime({ name: 'forest', durationMs: performance.now() - _t });

  _t = performance.now();
  drawTerritories();
  recordSectionTime({ name: 'territory', durationMs: performance.now() - _t });

  _t = performance.now();
  drawObjects({ visibleChunks });
  recordSectionTime({ name: 'objects', durationMs: performance.now() - _t });

  _t = performance.now();
  drawConstructionSites({ visibleChunks });
  recordSectionTime({ name: 'construction', durationMs: performance.now() - _t });

  _t = performance.now();
  drawGroundItems();
  recordSectionTime({ name: 'items', durationMs: performance.now() - _t });

  _t = performance.now();
  drawCorpses();
  recordSectionTime({ name: 'corpses', durationMs: performance.now() - _t });

  _t = performance.now();
  drawDebugOverlays({ visibleChunks });
  recordSectionTime({ name: 'debugOverlays', durationMs: performance.now() - _t });

  _t = performance.now();
  drawPaths();
  recordSectionTime({ name: 'paths', durationMs: performance.now() - _t });

  _t = performance.now();
  drawNpcs();
  recordSectionTime({ name: 'npcs', durationMs: performance.now() - _t });

  if (showPawns) {
    _t = performance.now();
    drawPawns();
    recordSectionTime({ name: 'pawns', durationMs: performance.now() - _t });
  }

  _t = performance.now();
  drawForestCanopy({ visibleChunks });
  recordSectionTime({ name: 'forestCanopy', durationMs: performance.now() - _t });

  _t = performance.now();
  drawProjectiles();
  recordSectionTime({ name: 'projectiles', durationMs: performance.now() - _t });

  _t = performance.now();
  drawSelectionHighlights();
  recordSectionTime({ name: 'selection', durationMs: performance.now() - _t });

  if (showFog) {
    _t = performance.now();
    drawFog();
    recordSectionTime({ name: 'fog', durationMs: performance.now() - _t });
  }

  if (showEnvironment) {
    _t = performance.now();
    drawEnvironment({ visibleChunks });
    recordSectionTime({ name: 'environment', durationMs: performance.now() - _t });
  }

  _t = performance.now();
  drawSelectionBox();
  recordSectionTime({ name: 'selectionBox', durationMs: performance.now() - _t });

  _t = performance.now();
  drawDebugHUD();
  recordSectionTime({ name: 'debugHUD', durationMs: performance.now() - _t });
};

export const render = (): void => {
  const visibleChunks = getVisibleChunks();
  requestVisibleChunks({ visibleChunks });

  if (isProfilerEnabled()) {
    renderLayersProfiled({ visibleChunks });
  } else {
    renderLayers({ visibleChunks });
  }
};

const requestVisibleChunks = ({
  visibleChunks,
}: {
  visibleChunks: ReadonlyArray<VisibleChunk>;
}): void => {
  const chunksToRequest = getChunksToRequest({ visibleChunks });
  refillChunkRequestTokens();
  const requestBudget = getChunkRequestBudget();
  if (requestBudget <= 0) {
    return;
  }

  // Limit requests per frame to avoid flooding
  for (let i = 0; i < Math.min(chunksToRequest.length, requestBudget); i++) {
    const chunk = chunksToRequest[i];
    if (chunk) {
      requestChunk({ chunkX: chunk.x, chunkY: chunk.y });
      chunkRequestTokens = Math.max(0, chunkRequestTokens - 1);
    }
  }
};

const refillChunkRequestTokens = (): void => {
  const nowMs = performance.now();
  const elapsedMs = nowMs - chunkRequestLastRefillMs;
  chunkRequestLastRefillMs = nowMs;

  chunkRequestTokens = Math.min(
    CHUNK_REQUEST_BURST,
    chunkRequestTokens + (elapsedMs / 1000) * CHUNK_REQUEST_RATE_PER_SECOND
  );
};

const getChunkRequestBudget = (): number => {
  const tokenBudget = Math.floor(chunkRequestTokens);
  if (tokenBudget <= 0) {
    return 0;
  }

  return Math.min(MAX_CHUNK_REQUESTS_PER_FRAME, tokenBudget);
};

// ============================================================================
// Visible Chunks Calculation (with caching)
// ============================================================================

const getVisibleChunks = (): ReadonlyArray<VisibleChunk> => {
  const camera = getCamera();
  const dims = getScreenDimensions();

  const halfWidth = dims.width / 2 / camera.zoom;
  const halfHeight = dims.height / 2 / camera.zoom;

  const minChunkX = Math.floor((camera.x - halfWidth) / (CHUNK_SIZE * TILE_SIZE));
  const maxChunkX = Math.ceil((camera.x + halfWidth) / (CHUNK_SIZE * TILE_SIZE));
  const minChunkY = Math.floor((camera.y - halfHeight) / (CHUNK_SIZE * TILE_SIZE));
  const maxChunkY = Math.ceil((camera.y + halfHeight) / (CHUNK_SIZE * TILE_SIZE));

  // Check if bounds changed - if not, return cached result (zero allocation)
  if (
    _cachedChunkBounds.minX === minChunkX &&
    _cachedChunkBounds.maxX === maxChunkX &&
    _cachedChunkBounds.minY === minChunkY &&
    _cachedChunkBounds.maxY === maxChunkY
  ) {
    return _visibleResult;
  }

  // Update cache bounds
  _cachedChunkBounds.minX = minChunkX;
  _cachedChunkBounds.maxX = maxChunkX;
  _cachedChunkBounds.minY = minChunkY;
  _cachedChunkBounds.maxY = maxChunkY;

  // PERFORMANCE: Reset length (V8 keeps backing store) and re-push from pool
  _visibleResult.length = 0;
  for (let y = minChunkY; y <= maxChunkY; y++) {
    for (let x = minChunkX; x <= maxChunkX; x++) {
      if (_visibleResult.length < MAX_VISIBLE_CHUNKS) {
        const chunk = _chunkPool[_visibleResult.length];
        if (chunk) {
          chunk.x = x;
          chunk.y = y;
          _visibleResult.push(chunk);
        }
      }
    }
  }

  return _visibleResult;
};

// ============================================================================
// Re-exports from Camera (for external use)
// ============================================================================

export {
  centerOnPosition,
  centerOnPositionWithZoom,
  getCamera,
  panCamera,
  screenToWorld,
  setZoom,
  worldToScreen,
  zoomAtPoint,
} from './Camera';
